import type { Countdown } from "@/utils/formatTime";
import type { MaybeReadonlyRefOrGetter } from "~/types/Utils";

type CountdownUrgency = "urgent" | "high" | "low";
type Precision = "seconds" | "relative";

/**
 * A composable/hook that provides a countdown from now until a given time
 * @param endDate a reactive object with a Date value
 * @returns a reactive object with the time left until the given endDate
 */
export const useCountdown = (
  endDate: MaybeRefOrGetter<Date>,
  callbacks?: {
    countdownFinished?: () => void;
    countdownRunningOut?: () => void;
  },
  /**
   * The precision of the countdown, will be seconds by default.
   * If set to "relative", it will depend on the total time remaining.
   */
  precision?: MaybeReadonlyRefOrGetter<Precision | undefined>,
) => {
  const getTimeLeft = () => {
    const deadline = new Date(toValue(endDate));

    return formatTime(deadline.valueOf() - Date.now());
  };

  const timeLeft: Ref<Countdown | ""> = ref(getTimeLeft());
  const isInitiallyFinished = toValue(endDate) <= new Date(Date.now());

  type IntervalIdType = ReturnType<typeof setTimeout>;
  let intervalId: IntervalIdType | null = null;

  const intervalMs = computed(() => {
    if (!toValue(precision) || toValue(precision) === "seconds") return 1000;

    if (!timeLeft.value) return 1000;

    const { days, hours } = timeLeft.value;

    if (days > 0) return 1000 * 60 * 60; // 1 hour

    if (hours > 0) return 1000 * 60; // 1 minute

    return 1000;
  });

  const updateTimeLeft = () => {
    timeLeft.value = getTimeLeft();
    setTimer();
  };

  const setTimer = () => {
    // add the ms difference to nearest round second for 1ms accuracy:
    const ms = +new Date() % 1000;
    intervalId = setTimeout(() => updateTimeLeft(), intervalMs.value - ms);
  };

  const countdownUrgency = computed<CountdownUrgency>(() => {
    if (!timeLeft.value || timeLeft.value.days >= 1) return "low";

    if (timeLeft.value.hours >= 2) return "high";

    return "urgent";
  });

  const colorCodedClass = computed(() => {
    const countdownUrgencyColorMapper: Record<CountdownUrgency, string> = {
      low: "text-platform-text-3",
      high: "text-platform-warning",
      urgent: "text-platform-danger",
    };

    return countdownUrgencyColorMapper[countdownUrgency.value];
  });

  watch(endDate, updateTimeLeft);

  const unwatch = watch(timeLeft, () => {
    if (isInitiallyFinished) return;

    if (timeLeft.value) {
      const { days, hours, minutes, seconds } = timeLeft.value;

      if (days === 0 && hours === 0 && minutes === 0 && seconds === 15) {
        // means there are 15 seconds left and the timer is almost running out.
        // Should refetch the product and bids at this moment.
        callbacks?.countdownRunningOut?.();
      }
    }

    if (timeLeft.value === "") {
      // means it's finished and did not initialize as finished
      callbacks?.countdownFinished?.();
      unwatch();
    }
  });

  onMounted(() => {
    updateTimeLeft();
  });

  onUnmounted(() => {
    if (intervalId) {
      clearInterval(intervalId);
    }
  });

  return {
    timeLeft,
    isInitiallyFinished,
    urgency: countdownUrgency,
    colorCodedClass,
  };
};
